import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { QInputForm } from '@kubiq-base/core';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAppContext } from '../../state/AppContext';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import { IAuthenticate } from '../../interfaces/';
import CenteredQButton from '../Shared/CenteredQButton';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';

interface Props {
  email: string;
  reset: () => void;
}

const PasswordForm = ({ email, reset }: Props): JSX.Element => {
  const { t } = useTranslation(TranslationNamespaces.login);
  const { postRequest } = useApi();
  const { setTokens } = useAppContext();
  const { getCache } = useCache();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ password: string }>();

  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState(false);

  const navigate = useNavigate();
  const session = getCache();

  const validatePassword = (value: string): string | undefined => {
    setErrorMessage(undefined);
    let error;

    if (value === '') error = 'requiredField';

    return error;
  };

  const onSubmit = async (data: { password: string }): Promise<void> => {
    if (data.password) {
      await postRequest<{ data: IAuthenticate }>(
        'Login/Authenticate',
        { email: email, password: data.password, accountType: 'web' },
        setLoading,
        ['invalidUser'],
      )
        .then((result) => {
          if (result.data) {
            const pathname = result.data.twoFactorEnabled ? '/Auth/2FA' : '/Enable2FA';
            setTokens(result.data);
            navigate(pathname);
          }
        })
        .catch(() => setErrorMessage('wrongPassword'));
    }
  };

  return (
    <>
      <HeaderWithDescription bigHeader translationNamespace={TranslationNamespaces.login}>
        <MessageBox text={email} emailBox />
      </HeaderWithDescription>

      <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
        <QInputForm
          control={control}
          name='password'
          label={t('password')}
          type='password'
          showPasswordToggle
          autoFocus={true}
          rules={{ validate: validatePassword }}
          isError={(errors.password && t(errors.password?.message as string)) || (errorMessage && t(errorMessage))}
        />

        <CenteredQButton type='submit' isLoading={loading} fullWidth color='secondary'>
          {t('next')}
        </CenteredQButton>
      </FlexFormContainer>

      <FlexContainer>
        <CenteredQButton to={session.verifiedPhone ? '/Recovery/Password' : '/Recovery/NotVerified'} variant='text'>
          {t('forgotPassword')}
        </CenteredQButton>

        <CenteredQButton variant='text' onClick={reset}>
          {t('cancel')}
        </CenteredQButton>
      </FlexContainer>
    </>
  );
};

export default PasswordForm;
